/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */

import { jsx, css } from "@emotion/core"
import {
  useState,
  FC,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
  CSSProperties,
} from "react"
import { Spin, Select, Input, Pagination } from "@duxton-web/ui"
import { SearchUniversal } from "@duxton-web/icons"
import { SelectProps } from "@duxton-web/ui/es/select"
import { InputRef, SearchProps } from "@duxton-web/ui/es/input"
import { PaginationProps } from "@duxton-web/ui/es/pagination"
import { useTranslationsMap } from "common/utils/i18n"
import { debounce } from "lodash"

import { useKeyPress } from "../../hooks"

export type OptionValue = {
  label: string
  value: string | number
  customLabelNode?: React.ReactNode
  disabled?: boolean
}

interface CustomPaginationProps extends PaginationProps {
  showPagination?: boolean
}
interface CustomSelectProps extends SelectProps {
  options?: OptionValue[]
  optGroups?: {
    label: ReactNode
    options: OptionValue[]
  }[]
  searchable?: boolean
  searchProps?: SearchProps
  searchDelayMs?: number
  customDropdownStyle?: CSSProperties
  paginationProps?: CustomPaginationProps
  filterOption?: boolean
  error?: string
  mode?: "multiple"
}

const { OptGroup, Option } = Select

const standardDropdownCssStyle = css`
  .dui-select-item-group {
    display: flex;
    align-items: center;
  }
`

const errorMessage = css`
  color: #ee6352;
  margin-top: 2px;
`

export const CustomSelect: FC<CustomSelectProps> = ({
  options,
  optGroups,
  searchable,
  searchProps,
  onSearch: onSearchProp,
  searchDelayMs = 1000,
  loading,
  customDropdownStyle,
  paginationProps = { showPagination: true },
  mode,
  onFocus,
  filterOption = true,
  error,
  ...selectProps
}: CustomSelectProps) => {
  const searchInputRef = useRef<InputRef>(null)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [filteredOptGroups, setFilteredOptGroups] = useState(optGroups)

  const tabPress = useKeyPress(9)

  const onSearch = useCallback(
    debounce(onSearchProp ?? (() => {}), searchDelayMs),
    [onSearchProp, searchDelayMs],
  )

  useEffect(() => {
    if (!filterOption) {
      setFilteredOptions(options)
      setFilteredOptGroups(optGroups)
      return
    }

    setFilteredOptions(
      options?.filter?.(option =>
        option.label.toLocaleLowerCase().includes(search.toLowerCase()),
      ),
    )
    setFilteredOptGroups(
      optGroups?.map(optGroup => ({
        ...optGroup,
        options: optGroup?.options.filter?.(option =>
          option.label.toLowerCase().includes(search.toLowerCase()),
        ),
      })),
    )
  }, [search, optGroups, options, filterOption])

  useEffect(() => {
    if (open) {
      // small hack for duxton, because we need to wait until it is shown
      setTimeout(() => {
        searchInputRef.current?.input?.focus()
      }, 100)
    } else if (search) {
      setSearch("")
      onSearch("")
      onSearch.flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    // close dropdown when "tab" is pressed
    if (tabPress && open) {
      setOpen(false)
    }
  }, [tabPress, open])

  const textMap = useTranslationsMap({
    searchNotFound: "menu.stores.noResultFound",
    searchPlaceholder: "common.search",
  })
  return (
    <Select
      showSearch={false}
      defaultActiveFirstOption={false}
      listHeight={240}
      mode={mode}
      data-testid="select"
      css={css`
        display: block;
      `}
      open={open}
      onDropdownVisibleChange={visible => {
        if (visible) {
          onSearch("")
          onSearch.flush()
        }

        setOpen(visible)
      }}
      dropdownStyle={customDropdownStyle}
      onFocus={onFocus}
      onInputKeyDown={e => e.stopPropagation()} // prevent backspace delete
      dropdownRender={menu => (
        <div css={standardDropdownCssStyle}>
          {searchable && (
            <div
              data-testid="search-input-container"
              css={css`
                padding: 16px;
              `}
            >
              <Input
                ref={searchInputRef}
                autoFocus
                onKeyDown={e => e.stopPropagation()} // prevent backspace delete
                data-testid="search-input"
                prefix={<SearchUniversal />}
                value={search}
                onChange={e => {
                  setSearch(e.target.value)
                  onSearch(e.target.value)
                }}
                placeholder={textMap.searchPlaceholder}
                {...searchProps}
              />
              {error && <div css={errorMessage}>{error}</div>}
            </div>
          )}
          {menu}
          {paginationProps?.showPagination && (
            <Pagination
              data-testid="search-pagination"
              css={css`
                text-align: center;

                .dui-pagination-simple-pager {
                  display: none !important;
                }
              `}
              simple
              showSizeChanger={false}
              {...paginationProps}
            />
          )}
        </div>
      )}
      notFoundContent={
        search &&
        !loading && (
          <div
            css={css`
              text-align: center;
            `}
          >
            {textMap.searchNotFound.replace(/<x>/i, search)}
          </div>
        )
      }
      {...selectProps}
    >
      {loading && (
        <Option key="loading" disabled>
          <Spin
            css={css`
              width: 100%;
            `}
          />
        </Option>
      )}
      {!loading &&
        filteredOptions?.map?.(option => (
          <Option
            key={option.value}
            value={option.value}
            title={option.label}
            disabled={option.disabled}
          >
            {option.customLabelNode ?? option.label}
          </Option>
        ))}
      {!loading &&
        filteredOptGroups?.map((optGroup, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <OptGroup key={i} label={optGroup.label}>
            {optGroup.options.map(option => (
              <Option
                key={option.value}
                value={option.value}
                title={option.label}
                disabled={option.disabled}
              >
                {option.customLabelNode ?? option.label}
              </Option>
            ))}
          </OptGroup>
        ))}
    </Select>
  )
}
